/** Local demo data when REACT_APP_ADMIN_LOGS_WALKTHROUGH=1 (no backend required). */

export const WALKTHROUGH_LOG_TYPES = [
  "api_http",
  "redemption_change",
  "n8n_redemption_webhook",
  "force_xero_bills",
  "admin_log_payload_view",
];

const MOCK_ROWS = [
  {
    _id: "674000000000000000000001",
    logType: "api_http",
    region: "AU",
    requestUrl: "/v1/redemption/verify",
    requestType: "POST",
    method: "POST",
    route: "/v1/redemption/verify",
    statusCode: 200,
    durationMs: 842,
    responseSummary: "Redemption verified",
    actorEmail: "staff@example.com",
    actorName: "Demo Admin",
    actorRole: "Administrator",
    customerId: "cust_abc123",
    merchantStoreId: "store_joondalup",
    vehicleRego: "BNN310",
    s3PayloadKey: "api-logs/au/2026/11/01/674000000000000000000001.json.enc",
    s3PayloadBytes: 2048,
    createdAt: "2026-11-01T00:15:00.000Z",
  },
  {
    _id: "674000000000000000000002",
    logType: "redemption_change",
    region: "AU",
    requestUrl: "/v1/redemption/6740/change-status",
    requestType: "PATCH",
    method: "PATCH",
    route: "/v1/redemption/:id/change-status",
    statusCode: 200,
    durationMs: 120,
    responseSummary: "Status updated to xero_pending",
    actorEmail: "ops@example.com",
    customerId: "cust_abc123",
    vehicleRego: "BNN310",
    s3PayloadKey: "api-logs/au/2026/11/01/674000000000000000000002.json.enc",
    createdAt: "2026-11-01T01:22:00.000Z",
  },
  {
    _id: "674000000000000000000003",
    logType: "n8n_redemption_webhook",
    region: "NZ",
    requestUrl: "/v1/redemption/webhook/n8n",
    requestType: "POST",
    method: "POST",
    route: "/v1/redemption/webhook/n8n",
    statusCode: 500,
    durationMs: 45,
    errorMessage: "Xero token expired",
    responseSummary: "Webhook failed",
    merchantStoreId: "store_auckland_cbd",
    vehicleRego: "ABC123",
    payloadStorageError: "S3 upload skipped in walkthrough",
    createdAt: "2026-11-01T02:40:00.000Z",
  },
  {
    _id: "674000000000000000000004",
    logType: "force_xero_bills",
    region: "AU",
    requestUrl: "/v1/admin/redemptions/force-xero",
    requestType: "POST",
    method: "POST",
    route: "/v1/admin/redemptions/force-xero",
    statusCode: 202,
    durationMs: 3100,
    responseSummary: "Queued 12 bills",
    actorEmail: "frank@example.com",
    actorRole: "Administrator",
    s3PayloadKey: "api-logs/au/2026/11/01/674000000000000000000004.json.enc",
    createdAt: "2026-11-01T03:05:00.000Z",
  },
];

const MOCK_PAYLOADS = {
  "674000000000000000000001": {
    request: { rego: "BNN310", storeId: "store_joondalup" },
    response: { success: true, redemptionId: "6740demo" },
    redactedSample: "[REDACTED] fields appear only inside stored payloads, not portal URLs",
  },
  "674000000000000000000002": {
    previousStatus: "pending",
    newStatus: "xero_pending",
    actor: "ops@example.com",
  },
  "674000000000000000000004": {
    redemptionIds: ["6740a", "6740b"],
    note: "Walkthrough mock payload (decrypted shape)",
  },
};

function matchesRegex(field, pattern) {
  if (!pattern) return true;
  try {
    return new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i").test(String(field || ""));
  } catch {
    return String(field || "").toLowerCase().includes(String(pattern).toLowerCase());
  }
}

export function filterWalkthroughLogs(query) {
  let rows = [...MOCK_ROWS];
  const {
    region,
    logType,
    method,
    statusCode,
    errorsOnly,
    route,
    q,
    actorEmail,
    customerId,
    merchantStoreId,
    rego,
    from,
    to,
  } = query;

  if (region) rows = rows.filter((r) => r.region === String(region).toUpperCase());
  if (logType) rows = rows.filter((r) => r.logType === logType);
  if (method) rows = rows.filter((r) => r.method === String(method).toUpperCase());
  if (statusCode) rows = rows.filter((r) => r.statusCode === Number(statusCode));
  if (errorsOnly === "true") {
    rows = rows.filter((r) => (r.statusCode && r.statusCode >= 400) || r.errorMessage);
  }
  if (route) rows = rows.filter((r) => matchesRegex(r.route, route));
  if (q) rows = rows.filter((r) => matchesRegex(r.requestUrl, q));
  if (actorEmail) rows = rows.filter((r) => matchesRegex(r.actorEmail, actorEmail));
  if (customerId) rows = rows.filter((r) => r.customerId === customerId);
  if (merchantStoreId) rows = rows.filter((r) => r.merchantStoreId === merchantStoreId);
  if (rego) rows = rows.filter((r) => matchesRegex(r.vehicleRego, rego));

  if (from) {
    const fromDate = new Date(from);
    rows = rows.filter((r) => new Date(r.createdAt) >= fromDate);
  }
  if (to) {
    const toDate = new Date(to);
    rows = rows.filter((r) => new Date(r.createdAt) <= toDate);
  }

  rows.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const page = Math.max(1, parseInt(query.page, 10) || 1);
  const limit = Math.min(100, Math.max(1, parseInt(query.limit, 10) || 25));
  const total = rows.length;
  const slice = rows.slice((page - 1) * limit, page * limit);

  return {
    page,
    limit,
    total,
    totalPages: Math.ceil(total / limit) || 1,
    logTypes: WALKTHROUGH_LOG_TYPES,
    rows: slice,
  };
}

export function getWalkthroughPayload(id) {
  const payload = MOCK_PAYLOADS[id];
  if (!payload) {
    return { success: false, message: "No payload stored for this log entry." };
  }
  return { success: true, data: { payload, source: "walkthrough_mock" } };
}
